import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { stateDisplayNameAndApiName, StatesDisplayAndAPI } from '../../../../../utilities/states';
import { phoneTypeOptions } from 'app/utilities/phone-types';
import { FormService } from 'app/services/form.service';
import { CountriesDisplayAndAPI, countryDisplayNameAndApiName } from 'app/utilities/countries';
import { FormValidator } from 'app/ui-components/form/form-validators/form-validators';
import { Subscription } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Component({
  selector: 'app-applicant-info',
  templateUrl: './applicant-info.component.html',
  styleUrls: ['./applicant-info.component.scss']
})
export class ApplicantInfoComponent implements OnInit, OnDestroy {
  @ViewChild('applicantInput') applicantInput!: ElementRef;
  @ViewChild('street1Input') street1Input!: ElementRef;
  @ViewChild('street2Input') street2Input!: ElementRef;
  @ViewChild('countryInput') countryInput!: ElementRef;
  @ViewChild('cityInput') cityInput!: ElementRef;
  @ViewChild('stateInput') stateInput!: ElementRef;
  @ViewChild('postalCodeInput') postalCodeInput!: ElementRef;
  @ViewChild('telephoneNumberInput') telephoneNumberInput!: ElementRef;
  @ViewChild('telephoneTypeInput') telephoneTypeInput!: ElementRef;
  @ViewChild('primaryContactFirstNameInput') primaryContactFirstNameInput!: ElementRef;
  @ViewChild('primaryContactLastNameInput') primaryContactLastNameInput!: ElementRef;
  @ViewChild('pronounsInput') pronounsInput!: ElementRef;
  @ViewChild('titleInput') titleInput!: ElementRef;
  @ViewChild('emailAddressInput') emailAddressInput!: ElementRef;
  states: StatesDisplayAndAPI[] = stateDisplayNameAndApiName;
  countries: CountriesDisplayAndAPI[] = countryDisplayNameAndApiName;
  phoneTypes = phoneTypeOptions;
  private sizeChangeSubscription!: Subscription;
  private mobileDeviceSize: boolean = false;
  errorMap: Map<string, boolean> = new Map();

  // Field values
  applicant!: string;
  street1!: string;
  street2!: string;
  country!: string;
  city!: string;
  state!: string;
  postalCode!: string;
  telephoneNumber!: string;
  telephoneType!: string;
  primaryContactFirstName!: string;
  primaryContactLastName!: string;
  pronouns!: string;
  title!: string;
  emailAddress!: string;

  previousCountry!: string; // So we can track when to remove state values.

  constructor(private formService: FormService, private formValidator: FormValidator, private responsive: BreakpointObserver) {}

  ngOnInit(): void {
    this.initializeFields();
    this.sizeChangeSubscription = this.responsive.observe([Breakpoints.Handset]).subscribe(result => {
      if(result.matches) {
        this.mobileDeviceSize = true;
      } else {
        this.mobileDeviceSize = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.sizeChangeSubscription.unsubscribe();
  }
  
  initializeFields(): void {
    this.applicant = this.applicantInfoForm.get('applicant')?.value;
    this.street1 = this.applicantInfoForm.get('street1')?.value;
    this.street2 = this.applicantInfoForm.get('street2')?.value;
    this.city = this.applicantInfoForm.get('city')?.value;
    this.state = this.applicantInfoForm.get('state')?.value;
    this.postalCode = this.applicantInfoForm.get('postalCode')?.value;
    this.previousCountry = this.applicantInfoForm.get('country')?.value;
    this.setCountryValue(this.previousCountry);
    this.telephoneNumber = this.applicantInfoForm.get('telephoneNumber')?.value;
    this.telephoneType = this.applicantInfoForm.get('telephoneType')?.value;
    this.primaryContactFirstName = this.applicantInfoForm.get('primaryContactFirstName')?.value;
    this.primaryContactLastName = this.applicantInfoForm.get('primaryContactLastName')?.value;
    this.pronouns = this.applicantInfoForm.get('pronouns')?.value;
    this.title = this.applicantInfoForm.get('title')?.value;
    this.emailAddress = this.applicantInfoForm.get('emailAddress')?.value;
  }

  public clearStreet1InputField() {
    this.errorCheck('street1');

    this.street1 = '';
    this.applicantInfoForm.patchValue({['street1']: ''});
  }
  public updateStreet1(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.street1 = '';
      }
    }
    this.errorCheck('street1');
    this.applicantInfoForm.patchValue({['street1']: this.street1});
  }

  public cancelStreet1Update() {
    this.errorCheck('street1');
    this.street1 = this.applicantInfoForm.get('street1')?.value;
  }

  public clearStreet2InputField() {
    this.street2 = '';
    this.applicantInfoForm.patchValue({['street2']: ''});
  }
  public updateStreet2(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.street2 = '';
      }
    }
    this.applicantInfoForm.patchValue({['street2']: this.street2});
  }

  public cancelStreet2Update() {
    this.street2 = this.applicantInfoForm.get('street2')?.value;
  }

  public clearCountryInputField() {
    this.errorCheck('country');
    this.country = '';
    this.applicantInfoForm.patchValue({['country']: ''});
    this.updatePreviousCountry();
  }

  public updateCountry(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.country = '';
      }
    }
    
    this.applicantInfoForm.patchValue({['country']: this.country});
    this.updatePreviousCountry();
    this.errorCheck('country');
  }

  private setCountryValue(country: string) {
    this.country = country;
    this.formValidator.addOrRemoveCountrySpecificValidation(this.applicantInfoForm);
  }
  
  public cancelCountryUpdate() {
    this.errorCheck('country');
    this.country = this.applicantInfoForm.get('country')?.value;
  }
  
  // Allows us to track the changes to country and clear out the state field if we're switching to/from USA.
  private updatePreviousCountry() {
    if(
      (this.country !== this.previousCountry) &&
      (
        (this.previousCountry && this.previousCountry === 'USA' && !this.isCountryUSA) ||
        (this.previousCountry && this.previousCountry !== 'USA' && this.isCountryUSA)
      )
    ) {
      this.state = '';
    }
    this.previousCountry = this.country;
    this.formValidator.addOrRemoveCountrySpecificValidation(this.applicantInfoForm);
  }

  public clearCityInputField() {
    this.errorCheck('city');
    this.city = '';
    this.applicantInfoForm.patchValue({['city']: ''});
  }
  
  public updateCity(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.city = '';
      }
    }
    this.errorCheck('city');
    this.applicantInfoForm.patchValue({['city']: this.city});
  }

  public cancelCityUpdate() {
    this.errorCheck('city');
    this.city = this.applicantInfoForm.get('city')?.value;
  }

  public clearStateInputField() {
    this.errorCheck('state');
    this.state = '';
    this.applicantInfoForm.patchValue({['state']: ''});
  }
  public updateState(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.state = '';
      }
    }
    
    this.applicantInfoForm.patchValue({['state']: this.state});
    this.errorCheck('state');
  }

  public cancelStateUpdate() {
    this.errorCheck('state');
    this.state = this.applicantInfoForm.get('state')?.value;
  }

  public clearPostalCodeInputField() {
    this.errorCheck('postalCode');
    this.postalCode = '';
    this.applicantInfoForm.patchValue({['postalCode']: ''});
  }
  public updatePostalCode(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.postalCode = '';
      }
    }
    this.errorCheck('postalCode');
    this.applicantInfoForm.patchValue({['postalCode']: this.postalCode});
  }

  public cancelPostalCodeUpdate() {
    this.errorCheck('postalCode');
    this.postalCode = this.applicantInfoForm.get('postalCode')?.value;
  }

  public clearTelephoneNumberInputField() {
    this.errorCheck('telephoneNumber');
    this.telephoneNumber = '';
    this.applicantInfoForm.patchValue({['telephoneNumber']: ''});
  }

  public updateTelephoneNumber(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.telephoneNumber = '';
      }
    }
    this.applicantInfoForm.patchValue({['telephoneNumber']: this.telephoneNumber});
    this.errorCheck('telephoneNumber');
  }

  public cancelTelephoneNumberUpdate() {
    this.telephoneNumber = this.applicantInfoForm.get('telephoneNumber')?.value;
    this.errorCheck('telephoneNumber');
  }

  public clearTelephoneTypeInputField() {
    this.errorCheck('telephoneType');
    this.telephoneType = '';
    this.applicantInfoForm.patchValue({['telephoneType']: ''});
  }

  public updateTelephoneType(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.telephoneType = '';
      }
    }
    this.applicantInfoForm.patchValue({['telephoneType']: this.telephoneType});
    this.errorCheck('telephoneType');
  }

  public cancelTelephoneTypeUpdate() {
    this.errorCheck('telephoneType');
    this.telephoneType = this.applicantInfoForm.get('telephoneType')?.value;
  }

  public clearPrimaryContactFirstNameInputField() {
    this.errorCheck('primaryContactFirstName');
    this.primaryContactFirstName = '';
    this.applicantInfoForm.patchValue({['primaryContactFirstName']: ''});
  }

  public updatePrimaryContactFirstName(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.primaryContactFirstName = '';
      }
    }
    this.errorCheck('primaryContactFirstName');
    this.applicantInfoForm.patchValue({['primaryContactFirstName']: this.primaryContactFirstName});
  }

  public cancelPrimaryContactFirstNameUpdate() {
    this.errorCheck('primaryContactFirstName');
    this.primaryContactFirstName = this.applicantInfoForm.get('primaryContactFirstName')?.value;
  }

  public clearPrimaryContactLastNameInputField() {
    this.errorCheck('primaryContactLastName');
    this.primaryContactLastName = '';
    this.applicantInfoForm.patchValue({['primaryContactLastName']: ''});
  }

  public updatePrimaryContactLastName(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.primaryContactLastName = '';
      }
    }
    this.errorCheck('primaryContactLastName');
    this.applicantInfoForm.patchValue({['primaryContactLastName']: this.primaryContactLastName});
  }

  public cancelPrimaryContactLastNameUpdate() {
    this.errorCheck('primaryContactLastName');
    this.primaryContactLastName = this.applicantInfoForm.get('primaryContactLastName')?.value;
  }

  public clearPronounsInputField() {
    this.pronouns = '';
    this.applicantInfoForm.patchValue({['pronouns']: ''});
  }
  public updatePronouns(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.pronouns = '';
      }
    }
    this.applicantInfoForm.patchValue({['pronouns']: this.pronouns});
  }

  public cancelPronounsUpdate() {
    this.pronouns = this.applicantInfoForm.get('pronouns')?.value;
  }

  public clearTitleInputField() {
    this.title = '';
    this.applicantInfoForm.patchValue({['title']: ''});
  }

  public updateTitle(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.title = '';
      }
    }
    this.applicantInfoForm.patchValue({['title']: this.title});
  }

  public cancelTitleUpdate() {
    this.title = this.applicantInfoForm.get('title')?.value;
  }

  public clearEmailAddressInputField() {
    this.emailAddress = '';
    this.applicantInfoForm.patchValue({['emailAddress']: ''});
    this.errorCheck('emailAddress');
  }

  public updateEmailAddress(event: Event) {
    if(event && event.type === 'blur') {
      var target = document.activeElement;
      if(target && target.nodeValue === "cancel") {
        this.emailAddress = '';
      }
    }
    this.errorCheck('emailAddress');
    this.applicantInfoForm.patchValue({['emailAddress']: this.emailAddress});
  }

  public cancelEmailAddressUpdate() {
    this.errorCheck('emailAddress');
    this.emailAddress = this.applicantInfoForm.get('emailAddress')?.value;
  }

   private errorCheck(fieldName) {
    const targetControl = this.applicantInfoForm.get(fieldName);
    
    if(!targetControl) {
      return;
    }

    // Force re-validation
    setTimeout(() => {
      targetControl.markAsTouched();
      targetControl.markAsDirty();
      targetControl.updateValueAndValidity();
      this.errorMap.set(fieldName, !targetControl.valid);
    });
  }

  get currentForm(): FormGroup {
    return this.formService.getCurrentUsanFormValue();
  }

  get applicantInfoForm(): FormGroup {
    return this.currentForm.get('applicantInfo') as FormGroup;
  }
  
  public hasFieldValidationError(fieldName: string, errorName?: string): boolean {
    return this.formService.hasFieldValidationError(fieldName, errorName, 'applicantInfo');
  } 
  
  get isCountryUSA(): boolean {
    return 'USA' === this.applicantInfoForm.get('country')?.value;
  }

  get isMobileDeviceSize() : boolean {
    return this.mobileDeviceSize;
  }
}
